import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { selectPOIById } from "@/features/survey/poisSelectors";
import { selectCurrentSurveyId } from "@/features/survey/surveySelectors";
import {
  surveyPOIApi,
  useGetPOICategoryQuery,
  useUpdatePOICategoryMutation,
} from "@/services/survey-api";
import { EmeraldCheckbox } from "@emerald-react/checkbox";
import { EmeraldFileUpload } from "@emerald-react/file-upload";
import { EmeraldSelect } from "@emerald-react/select";
import { updateCategoryField } from "@features/survey/poisSlice";
import { useCurrentPoiId } from "@hooks/useSurveySelectors";
import { mediaUpload, useUploadOwnerMediaFileMutation } from "@services/media";
import type { MediaFile } from "@survey-types/survey.media.types";
import React from "react";

const PoiLogoSection = () => {
  const surveyId = useAppSelector(selectCurrentSurveyId);
  const dispatch = useAppDispatch();
  const currentPoiId = useCurrentPoiId();
  const currentPOI = useAppSelector((state) =>
    selectPOIById(currentPoiId)(state),
  );
  const categoryId = currentPOI?.categoryId || "";
  const category = currentPOI?.category;

  useGetPOICategoryQuery(
    { surveyId: surveyId || "", categoryId },
    { skip: !surveyId || !categoryId },
  );

  const [updatePOICategory] = useUpdatePOICategoryMutation();
  const [uploadOwnerMediaFile] = useUploadOwnerMediaFileMutation();

  const logoLinks = category?.logoData?.logoLinks || [];
  const logoOptions = logoLinks.map((link) => ({
    value: link.logoId || link.url || "",
    label: link.displayName || "Logo",
  }));

  const selectedLogoLink = logoLinks.find((l) => l.isActive === true);
  const selectedLogoId =
    selectedLogoLink?.logoId || selectedLogoLink?.url || "";

  const handleLogoChange = (
    _event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    value: string | string[],
  ) => {
    const chosenId = value as string;

    // Find the logoId (UUID) from our logoLinks if it's there
    const targetLink = logoLinks.find(
      (l) => l.logoId === chosenId || l.url === chosenId,
    );
    const logoIdToSubmit = targetLink?.logoId || chosenId;

    // Optimistic Redux update — reflect isActive change on all links locally
    const updatedLinks = logoLinks.map((l) => ({
      ...l,
      isActive: l.logoId === chosenId || l.url === chosenId,
    }));
    dispatch(
      updateCategoryField({
        categoryId,
        updates: {
          logoData: { ...category?.logoData, logoLinks: updatedLinks },
        },
      }),
    );

    // Optimistic RTK Query cache update
    dispatch(
      surveyPOIApi.util.updateQueryData(
        "getPOICategories",
        surveyId || "",
        (draft) => {
          const cat = draft.find((c) => c.extId === categoryId);
          if (cat?.logoData) {
            cat.logoData.logoLinks = updatedLinks;
          }
        },
      ),
    );

    // API payload — send the newly active link with its ID and isActive true
    updatePOICategory({
      surveyId: surveyId || "",
      categoryId,
      body: {
        logoData: {
          logoLinks: [{ logoId: logoIdToSubmit, isActive: true }],
        },
      },
    });
  };

  const handleWhiteBackgroundChange = (checked: boolean) => {
    // Optimistic Redux update
    dispatch(
      updateCategoryField({
        categoryId,
        updates: {
          logoData: { ...category?.logoData, logoWhiteBackground: checked },
        },
      }),
    );

    // Optimistic RTK Query cache update
    dispatch(
      surveyPOIApi.util.updateQueryData(
        "getPOICategories",
        surveyId || "",
        (draft) => {
          const cat = draft.find((c) => c.extId === categoryId);
          if (cat?.logoData) {
            cat.logoData.logoWhiteBackground = checked;
          }
        },
      ),
    );

    // Persist to backend
    updatePOICategory({
      surveyId: surveyId || "",
      categoryId,
      body: { logoData: { logoWhiteBackground: checked } },
    });
  };

  const handleFileUpload = async (_event: unknown, files: File[]) => {
    if (!surveyId || !categoryId || !files.length) return;
    try {
      const uploaded: MediaFile[] = await Promise.all(
        files.map((file) =>
          mediaUpload(file, surveyId, "POILOGO", uploadOwnerMediaFile),
        ),
      );

      // Requirement: link with poi using createCategory (here updatePOICategory as category exists)
      // providing url, displayName, isActive: true
      const newLinks = uploaded.map((media) => ({
        url: media.cloudfrontUrl || media.s3Url || "",
        displayName: media.displayName || "",
        isActive: true,
      }));

      // Optimistically update Redux (optional but good for UX)
      dispatch(
        updateCategoryField({
          categoryId,
          updates: {
            logoData: {
              ...category?.logoData,
              logoLinks: [
                ...(category?.logoData?.logoLinks || []).map((l) => ({
                  ...l,
                  isActive: false,
                })),
                ...newLinks,
              ],
            },
          },
        }),
      );

      await updatePOICategory({
        surveyId,
        categoryId,
        body: {
          logoData: {
            logoLinks: newLinks,
          },
        },
      });
    } catch (error) {
      console.error("Failed to upload POI logo:", error);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Uploaded Logos dropdown */}
      <div className="flex flex-col gap-y-1">
        <div className="text-sm">Uploaded Logos</div>
        <EmeraldSelect
          label=""
          value={selectedLogoId}
          options={logoOptions}
          onChange={handleLogoChange}
          className="w-full"
        />
      </div>

      {/* Preview – shown only when a logo is active */}
      {selectedLogoLink?.url && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <span className="text-sm text-data">Preview</span>
            {category?.logoData?.logoWhiteBackground && (
              <span className="text-xs bg-neutral-200 text-neutral-600 rounded px-1.5 py-0.5 leading-tight">
                White background applied
              </span>
            )}
          </div>
          <div
            className="border border-divider-3 rounded p-3 flex items-center justify-center"
            style={{
              backgroundImage:
                "linear-gradient(45deg, #ccc 25%, transparent 25%), " +
                "linear-gradient(-45deg, #ccc 25%, transparent 25%), " +
                "linear-gradient(45deg, transparent 75%, #ccc 75%), " +
                "linear-gradient(-45deg, transparent 75%, #ccc 75%)",
              backgroundSize: "12px 12px",
              backgroundPosition: "0 0, 0 6px, 6px -6px, -6px 0px",
            }}
          >
            <img
              src={selectedLogoLink.url}
              alt={selectedLogoLink.displayName || "Logo preview"}
              className="h-10 max-w-40 object-contain"
              style={
                category?.logoData?.logoWhiteBackground
                  ? { background: "white", padding: "2px" }
                  : {}
              }
            />
          </div>
        </div>
      )}

      {/* Show white background checkbox */}
      <EmeraldCheckbox
        id="logoWhiteBackground"
        checked={category?.logoData?.logoWhiteBackground || false}
        label="Show white background on logo"
        onChange={handleWhiteBackgroundChange}
      />

      {/* File upload */}
      <EmeraldFileUpload
        allowMultiple={true}
        allowDragAndDrop={true}
        acceptedFileTypes={["image/jpeg", "image/png", "image/avif"]}
        inputLabel="Select image(s) or drop here to upload"
        onChange={handleFileUpload}
        onDrop={handleFileUpload}
        variant="labelOnTop"
      />
    </div>
  );
};

export default PoiLogoSection;
